import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, X, Settings, Plus, Trash2, TrendingUp } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { getMonthGrid } from '../utils/calendarGrid';
import { getContrastColor } from '../utils/color';


function koreaToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function fmt(n) {
  if (!n) return '0';
  return n.toLocaleString('ko-KR');
}

// ── 채널 관리 모달 ─────────────────────────────────────────────────
function ChannelModal({ channels, onClose, onRefresh }) {
  const [form, setForm] = useState({ name: '', color: '#818cf8' });
  const COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399'];

  const add = async () => {
    if (!form.name.trim()) return;
    await api.post('/sales/channels', form);
    setForm({ name: '', color: '#818cf8' });
    onRefresh();
  };

  const remove = async (id) => {
    if (!confirm('채널을 삭제하면 관련 기록도 삭제됩니다. 계속할까요?')) return;
    await api.delete(`/sales/channels/${id}`);
    onRefresh();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 w-80 max-h-[70vh] flex flex-col gap-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-white text-sm">판매채널 관리</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
        </div>

        <div className="flex flex-col gap-2 overflow-y-auto flex-1">
          {channels.map(ch => (
            <div key={ch.id} className="flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ch.color }} />
              <span className="flex-1 text-sm text-gray-200">{ch.name}</span>
              <button onClick={() => remove(ch.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={13} /></button>
            </div>
          ))}
          {channels.length === 0 && <p className="text-gray-600 text-xs text-center py-2">채널 없음</p>}
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-800 pt-3">
          <div className="flex gap-2">
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && add()}
              placeholder="채널명 입력"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-600"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {COLORS.map(c => (
              <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                className={`w-5 h-5 rounded-full transition-transform ${form.color === c ? 'scale-125 ring-2 ring-white/50' : ''}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <button onClick={add}
            className="flex items-center justify-center gap-1 py-2 bg-brand text-white text-xs rounded-lg hover:bg-brand/80">
            <Plus size={12} /> 채널 추가
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 날짜 상세 패널 ─────────────────────────────────────────────────
function DayPanel({ date, onClose }) {
  const [channels, setChannels] = useState([]);
  const [products, setProducts] = useState([]);
  const [recordMap, setRecordMap] = useState({});
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/sales/day', { params: { date } });
    setChannels(r.data.channels);
    setProducts(r.data.products);
    setRecordMap(r.data.recordMap);
    setLoading(false);
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const getQty = (productId, channelId) => parseInt(recordMap[productId]?.[channelId]) || 0;

  const setQty = (productId, channelId, val) => {
    setRecordMap(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [channelId]: val },
    }));
  };

  const saveQty = (productId, channelId, val) => {
    const key = `${productId}-${channelId}`;
    clearTimeout(saveTimer.current[key]);
    saveTimer.current[key] = setTimeout(async () => {
      await api.put('/sales/record', { date, productId, channelId, quantity: parseInt(val) || 0 });
    }, 500);
  };

  const productTotal = (productId) => channels.reduce((s, ch) => s + (getQty(productId, ch.id) || 0), 0);
  const productRevenue = (productId) => {
    const p = products.find(p => p.id === productId);
    return productTotal(productId) * (p?.consumerPrice || 0);
  };
  const channelTotal = (channelId) => products.reduce((s, p) => s + (getQty(p.id, channelId) || 0), 0);
  const grandQty = () => products.reduce((s, p) => s + productTotal(p.id), 0);
  const grandRevenue = () => products.reduce((s, p) => s + productRevenue(p.id), 0);

  const [y, m, d] = date.split('-');
  const dateLabel = `${parseInt(m)}월 ${parseInt(d)}일`;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-40" onClick={onClose}>
      <div className="bg-gray-950 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:w-auto sm:min-w-[600px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}>

        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <div>
            <h3 className="font-bold text-white">{dateLabel} 판매현황</h3>
            {!loading && grandQty() > 0 && (
              <p className="text-xs text-brand mt-0.5">총 {fmt(grandQty())}개 · {fmt(grandRevenue())}원</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1"><X size={18} /></button>
        </div>

        {/* 테이블 */}
        <div className="overflow-auto flex-1 p-4">
          {loading ? (
            <p className="text-gray-600 text-sm text-center py-8">불러오는 중...</p>
          ) : channels.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-8">판매채널을 먼저 추가해주세요</p>
          ) : products.length === 0 ? (
            <p className="text-gray-600 text-sm text-center py-8">등록된 상품이 없습니다</p>
          ) : (
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="text-left text-xs text-gray-500 font-medium pb-2 pr-3 sticky left-0 bg-gray-950 min-w-[120px]">상품</th>
                  {channels.map(ch => (
                    <th key={ch.id} className="text-center text-xs font-medium pb-2 px-2 min-w-[70px]">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ch.color }} />
                        <span style={{ color: ch.color }}>{ch.name}</span>
                      </span>
                    </th>
                  ))}
                  <th className="text-right text-xs text-gray-500 font-medium pb-2 px-2 min-w-[50px]">수량</th>
                  <th className="text-right text-xs text-gray-500 font-medium pb-2 pl-2 min-w-[80px]">매출</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, pi) => (
                  <tr key={p.id} className={pi % 2 === 0 ? '' : 'bg-white/[0.02]'}>
                    <td className="py-1.5 pr-3 sticky left-0 bg-gray-950">
                      <span className="text-gray-200 text-xs leading-tight line-clamp-1">{p.name}</span>
                      {p.consumerPrice > 0 && (
                        <span className="text-gray-600 text-[10px] block">{fmt(p.consumerPrice)}원</span>
                      )}
                    </td>
                    {channels.map(ch => (
                      <td key={ch.id} className="text-center px-2 py-1">
                        <input
                          type="number"
                          min="0"
                          value={getQty(p.id, ch.id) || ''}
                          placeholder="0"
                          onChange={e => setQty(p.id, ch.id, e.target.value)}
                          onBlur={e => saveQty(p.id, ch.id, e.target.value)}
                          className="w-14 text-center bg-gray-800 border border-gray-700/50 rounded-lg px-1 py-1 text-sm text-white focus:outline-none focus:border-brand placeholder-gray-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </td>
                    ))}
                    <td className="text-right px-2 py-1 text-gray-300 text-sm font-medium">
                      {productTotal(p.id) > 0 ? fmt(productTotal(p.id)) : <span className="text-gray-700">-</span>}
                    </td>
                    <td className="text-right pl-2 py-1 text-gray-300 text-sm">
                      {productRevenue(p.id) > 0 ? <span className="text-brand">{fmt(productRevenue(p.id))}</span> : <span className="text-gray-700">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-800">
                  <td className="pt-2 pr-3 text-xs text-gray-500 font-medium sticky left-0 bg-gray-950">합계</td>
                  {channels.map(ch => (
                    <td key={ch.id} className="pt-2 text-center px-2 text-sm text-gray-400">
                      {channelTotal(ch.id) > 0 ? fmt(channelTotal(ch.id)) : <span className="text-gray-700">-</span>}
                    </td>
                  ))}
                  <td className="pt-2 text-right px-2 text-sm font-bold text-white">{fmt(grandQty())}</td>
                  <td className="pt-2 text-right pl-2 text-sm font-bold text-brand">{fmt(grandRevenue())}원</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 메인 캘린더 ───────────────────────────────────────────────────
export default function SalesCalendarPage() {
  const today = koreaToday();
  const [year, setYear]   = useState(() => parseInt(today.split('-')[0]));
  const [month, setMonth] = useState(() => parseInt(today.split('-')[1]) - 1);
  const [dailyRevenue, setDailyRevenue] = useState({});
  const [channels, setChannels] = useState([]);
  const [legendChannels, setLegendChannels] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showChannelModal, setShowChannelModal] = useState(false);

  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);
  const weeks = useMemo(() => {
    const r = [];
    for (let i = 0; i < grid.length; i += 7) r.push(grid.slice(i, i + 7));
    return r;
  }, [grid]);

  const loadChannels = useCallback(() => {
    api.get('/sales/channels').then(r => setChannels(r.data));
  }, []);

  const loadMonthly = useCallback(() => {
    if (!grid.length) return;
    const start = grid[0].dateStr;
    const end = grid[grid.length - 1].dateStr;
    const currentMonth = `${year}-${String(month + 1).padStart(2, '0')}`;
    api.get('/sales/monthly', { params: { start, end, currentMonth } })
      .then(r => { setDailyRevenue(r.data.dates); setLegendChannels(r.data.channels || []); });
  }, [year, month, grid]);

  useEffect(() => { loadChannels(); }, [loadChannels]);
  useEffect(() => { loadMonthly(); }, [loadMonthly]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const isThisMonth = year === parseInt(today.split('-')[0]) && month === parseInt(today.split('-')[1]) - 1;

  const monthTotal = useMemo(() => Object.values(dailyRevenue).reduce((s, v) => s + (v.total || 0), 0), [dailyRevenue]);
  const maxRevenue = useMemo(() => Math.max(...Object.values(dailyRevenue).map(v => v.total || 0), 1), [dailyRevenue]);

  const handleDayClick = (cell) => {
    setSelectedDate(cell.dateStr);
  };

  const handlePanelClose = () => {
    setSelectedDate(null);
    loadMonthly();
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-1">
          <TrendingUp size={20} className="text-brand flex-shrink-0 mr-1" />
          <button onClick={prevMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800"><ChevronLeft size={20} /></button>
          <h1 className="text-xl font-bold text-white min-w-[140px] text-center">{year}년 {month + 1}월</h1>
          <button onClick={nextMonth} className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800"><ChevronRight size={20} /></button>
          {isThisMonth ? (
            <span className="px-1.5 py-0.5 text-[10px] text-brand bg-brand/10 rounded font-medium ml-1">이번 달</span>
          ) : (
            <button onClick={() => { const t = today.split('-'); setYear(+t[0]); setMonth(+t[1] - 1); }}
              className="px-2 py-1 text-xs text-brand border border-brand/30 rounded hover:bg-brand/10 ml-1">이번 달로</button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {legendChannels.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {legendChannels.map(ch => (
                <span key={ch.id} className="flex items-center gap-1 text-xs text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: ch.color }} />
                  {ch.name}
                </span>
              ))}
            </div>
          )}
          {monthTotal > 0 && (
            <div className="flex flex-col items-end">
              <span className="text-[10px] text-gray-500">월 총 매출</span>
              <span className="text-base font-bold text-brand">{fmt(monthTotal)}원</span>
            </div>
          )}
          <button onClick={() => setShowChannelModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 text-gray-400 hover:text-white text-xs rounded-lg">
            <Settings size={13} /> 채널 관리
          </button>
        </div>
      </div>

      {/* 요일 헤더 */}
      <div className="grid grid-cols-7 border-b border-gray-800 flex-shrink-0">
        {WEEKDAYS.map((d, i) => (
          <div key={d} className={`text-center py-2.5 text-sm font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-500'}`}>{d}</div>
        ))}
      </div>

      {/* 캘린더 그리드 */}
      <div className="flex-1 min-h-0" style={{ display: 'grid', gridTemplateRows: `repeat(${weeks.length}, 1fr)` }}>
        {weeks.map((week, wi) => {
          const maxRows = Math.max(...week.map(cell => (dailyRevenue[cell.dateStr]?.items?.length || 0)), 0);
          return (
            <div key={wi} className="border-b border-gray-800/50 last:border-b-0 relative overflow-hidden">

              {/* 배경 클릭 레이어 */}
              <div className="absolute inset-0 grid grid-cols-7">
                {week.map((cell, di) => (
                  <div key={di}
                    onClick={() => handleDayClick(cell)}
                    className={`${di < 6 ? 'border-r border-gray-800/30' : ''}
                      ${cell.isCurrentMonth ? 'hover:bg-white/[0.03]' : 'bg-gray-950/30 hover:bg-white/[0.02]'} cursor-pointer
                      ${selectedDate === cell.dateStr ? 'bg-brand/10' : ''}`}
                  />
                ))}
              </div>

              {/* 날짜 + 일 매출 */}
              <div className="relative z-10 grid grid-cols-7 pointer-events-none">
                {week.map((cell, di) => {
                  const isToday = cell.dateStr === today;
                  const revenue = dailyRevenue[cell.dateStr]?.total || 0;
                  return (
                    <div key={di} className="px-1.5 pt-1 pb-0.5 flex items-center justify-between">
                      <span className={`text-sm font-semibold inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0
                        ${isToday ? 'bg-brand text-white'
                          : !cell.isCurrentMonth ? 'text-gray-600'
                          : di === 0 ? 'text-red-400'
                          : di === 6 ? 'text-blue-400'
                          : 'text-gray-300'}`}>
                        {cell.day}
                      </span>
                      {revenue > 0 && cell.isCurrentMonth && (
                        <span className="text-[10px] text-brand font-semibold truncate ml-1">{fmt(revenue)}원</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 채널 바 */}
              <div className="relative z-10 mt-0.5">
                {Array.from({ length: maxRows }, (_, ri) => (
                  <div key={ri} className="grid grid-cols-7" style={{ height: '26px' }}>
                    {week.map((cell, di) => {
                      const item = dailyRevenue[cell.dateStr]?.items?.[ri];
                      if (!item) return <div key={di} />;
                      const color = item.color || '#818cf8';
                      const opacity = cell.isCurrentMonth ? 1 : 0.4;
                      return (
                        <div key={di}
                          className="h-[23px] mx-[1px] flex items-center text-[11px] font-medium rounded-md cursor-pointer hover:brightness-125 transition-all"
                          style={{ backgroundColor: color, color: getContrastColor(color), opacity }}
                          onClick={() => setSelectedDate(cell.dateStr)}>
                          <span className="ml-1.5 truncate flex-1">{item.name}</span>
                          <span className="mr-1.5 flex-shrink-0 font-bold">{item.quantity}</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* 날짜 상세 패널 */}
      {selectedDate && (
        <DayPanel date={selectedDate} onClose={handlePanelClose} />
      )}

      {/* 채널 관리 모달 */}
      {showChannelModal && (
        <ChannelModal
          channels={channels}
          onClose={() => setShowChannelModal(false)}
          onRefresh={() => { loadChannels(); }}
        />
      )}
    </div>
  );
}
